import { fullName } from 'module/helpers/full-name';
import { HeadingCompact00 } from 'module/ui/components/styles';
import { PeopleListRow } from 'api/account-people/types';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  noRoleId,
  PeopleListPageParams,
} from 'components/organisms/account-people/utils/use-page-params';
import { AccountPeopleBoardUserCard } from './user-card';
import { AccountPeopleEmptyState } from '../empty-state/no-people';
import { AccountPeopleNoSearchResult } from '../empty-state/no-search-result';
import { useExternalTeamMemberRoleField } from '../utils/use-external-team-member-role-field';

const Title = styled.div`
  ${HeadingCompact00};
`;
const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: var(--spacing-05);
`;
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding: 0 var(--spacing-06);
`;

const Wrapper = styled.div`
  padding: var(--spacing-05) 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-07);
`;

type Props = {
  pageParams: PeopleListPageParams;
  clearSearch: VoidFunction;
  people: PeopleListRow[];
  lastMeetingParticipants: PeopleListRow[];
};

export const AccountPeopleBoard = ({
  pageParams,
  people,
  lastMeetingParticipants,
  clearSearch,
}: Props) => {
  const searchQueryfilterFunction = useCallback(
    (data: PeopleListRow) => {
      if (isEmpty(pageParams.searchQuery)) {
        return true;
      }

      const name = fullName(data.user);

      return (
        name
          .toLowerCase()
          .includes(pageParams.searchQuery?.toLowerCase() ?? '') ?? true
      );
    },
    [pageParams.searchQuery],
  );

  const { field: roleField } = useExternalTeamMemberRoleField();
  const roleName = useMemo(() => {
    return (
      roleField?.metaData?.options?.find(
        (role) => role.value === pageParams.role,
      )?.label ?? ''
    );
  }, [roleField, pageParams.role]);
  const filteredLastMeetingParticipants = useMemo(() => {
    return lastMeetingParticipants.filter(searchQueryfilterFunction) ?? [];
  }, [lastMeetingParticipants, searchQueryfilterFunction]);
  const filterPeopleList = useMemo(() => {
    return people.filter(searchQueryfilterFunction) ?? [];
  }, [people, searchQueryfilterFunction]);
  const isEmptyPeopleList = useMemo(() => {
    return people.length === 0;
  }, [people]);

  return (
    <Wrapper>
      {filteredLastMeetingParticipants.length > 0 && (
        <Section>
          <Title>
            <FormattedMessage id="account.people.board.sections.last_meeting_participants" />
          </Title>
          <Board>
            {filteredLastMeetingParticipants.map((data) => (
              <AccountPeopleBoardUserCard key={data.user.userId} data={data} />
            ))}
          </Board>
        </Section>
      )}
      {isEmptyPeopleList ? (
        <AccountPeopleEmptyState roleName={roleName} />
      ) : (
        <Section>
          <Title>
            {pageParams.role === noRoleId ? (
              <FormattedMessage id="all" />
            ) : (
              roleName
            )}
          </Title>
          {filterPeopleList.length === 0 ? (
            <AccountPeopleNoSearchResult onPrimaryAction={clearSearch} />
          ) : (
            <Board>
              {filterPeopleList.map((data) => (
                <AccountPeopleBoardUserCard
                  key={data.user.userId}
                  data={data}
                />
              ))}
            </Board>
          )}
        </Section>
      )}
    </Wrapper>
  );
};
